import { trackEvent } from '@/utils/analytics';
import { gamesLoader } from '@/utils/games-loader';
import React, { memo, useEffect, useState } from 'react';

interface GameLoaderProps {
    gameName: string;
}

export const GameLoader: React.FC<GameLoaderProps> = memo(({ gameName }) => {
    const [GameComponent, setGameComponent] = useState<React.ComponentType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadGame = async () => {
            try {
                setIsLoading(true);
                setError(null);

                console.log(`🎮 Loading game: ${gameName}`);
                const Component = await gamesLoader.loadGame(gameName);
                setGameComponent(() => Component);

                // Track game started
                trackEvent('game_started', {
                    game_name: gameName,
                });

                console.log(`✅ Game loaded successfully: ${gameName}`);
            } catch (err) {
                console.error(`❌ Failed to load game ${gameName}:`, err);
                setError(err instanceof Error ? err.message : 'Failed to load game');
            } finally {
                setIsLoading(false);
            }
        };

        loadGame();
    }, [gameName]);

    if (isLoading) {
        return (
            <div className='flex flex-col items-center justify-center h-full bg-vscode-bg text-vscode-text'>
                <div className='text-center'>
                    <div className='text-4xl mb-4 animate-bounce'>🎮</div>
                    <h2 className='text-xl font-semibold mb-2 text-vscode-accent'>Loading {gameName}...</h2>
                    <p className='text-vscode-text-muted'>Loading games bundle...</p>
                    <div className='mt-4 w-8 h-8 border-2 border-vscode-accent border-t-transparent rounded-full animate-spin mx-auto'></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex flex-col items-center justify-center h-full bg-vscode-bg text-vscode-text'>
                <div className='text-center'>
                    <div className='text-4xl mb-4'>❌</div>
                    <h2 className='text-xl font-semibold mb-2 text-red-400'>Failed to load game</h2>
                    <p className='text-vscode-text-muted mb-4'>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className='px-4 py-2 bg-vscode-accent text-white rounded hover:bg-vscode-accent-secondary transition-colors'
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!GameComponent) {
        return (
            <div className='flex flex-col items-center justify-center h-full bg-vscode-bg text-vscode-text'>
                <div className='text-center'>
                    <div className='text-4xl mb-4'>🤔</div>
                    <h2 className='text-xl font-semibold mb-2 text-vscode-accent'>Game not found</h2>
                    <p className='text-vscode-text-muted'>The game "{gameName}" could not be loaded.</p>
                </div>
            </div>
        );
    }

    return <GameComponent />;
});
